<script setup lang="ts">
/**
 * MY — EDD (Enhanced Customer Due Diligence, M32211) 문서-뷰 패널.
 *
 * CDD 미러링하되:
 *   - 단일 문서 타입 (EDD)
 *   - **Add EDD** 로 다건(사람마다) 인스턴스 추가
 *   - Entity 필수 — 해당 entity officer 조회
 */
import { ref, computed, provide, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail, SignerEntry } from '@/features/AI-documents/types/docGen.types';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import { EDD_KEYS, EDD_DOC_LABEL, EDD_TEMPLATE_ID, labelize, typeOf, isOptional } from './edd/eddFields';
import { CDD_DOC_CTX } from './cdd/cddContext';
import { buildEddVariables } from '../mappers/eddMapper';
import { resolveEddPersonEntityInfo } from './edd/eddEntityRoles';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import EddDoc from './edd/EddDoc.vue';

interface EddDocInst {
  templateId: string;
  values: Record<string, string>;
  checkbox: string;
  title: string;
  /** Word [[signers]] — 문서 인스턴스별 서명자 */
  signers: SignerEntry[];
}

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: Record<string, any> | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  activeAddressContext?: { fieldGroup: string; index: number } | null;
  editTaskId?: number | null;
  studio?: boolean;
  pendingDocType?: string;
  appliedAddress?: { key: string; line: string; town: string; state: string; postcode: string; country: string; seq: number } | null;
  pendingPick?: { value: string; seq: number } | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', ctx: Record<string, any>): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'progress-change', pct: number): void;
  (e: 'request-address-input', key: string): void;
}>();

const router = useRouter();
const { t } = useI18n();
const entityCd = computed(() => String(props.entityData?.entityCd || props.entityData?.icpCd || '').trim());

const taskTitleLocal = ref('');
const titleModel = computed({
  get: () => taskTitleLocal.value || props.taskTitle || '',
  set: (v: string) => { taskTitleLocal.value = v; },
});
const effectiveTitle = computed(() => titleModel.value.trim());

const setupDone = ref(false);
const wizardStep = ref(1);
const showWizard = computed(() => !props.studio && !setupDone.value);
function wizardNext() {
  if (wizardStep.value === 1 && !effectiveTitle.value) return;
  if (wizardStep.value === 2 && !entityCd.value) {
    progress.value = t('edd.errNoEntity');
    return;
  }
  if (wizardStep.value < 2) wizardStep.value += 1;
  else {
    if (!documents.value.length) addDoc();
    setupDone.value = true;
  }
}
function wizardBack() { if (wizardStep.value > 1) wizardStep.value -= 1; }

const documents = ref<EddDocInst[]>([]);
const activeIdx = ref(-1);
const activeDoc = computed(() => documents.value[activeIdx.value] || null);
const renamingIdx = ref(-1);

function addDoc() {
  documents.value.push({
    templateId: EDD_TEMPLATE_ID,
    values: {},
    checkbox: '',
    title: '',
    signers: [],
  });
  activeIdx.value = documents.value.length - 1;
  activeKey.value = '';
}

function setActiveSigners(list: SignerEntry[]) {
  if (!activeDoc.value) return;
  activeDoc.value.signers = Array.isArray(list) ? list : [];
}
/** 진입 시 최소 1건 — 빈 화면(Add EDD만) 두지 않음. */
function ensureDefaultDoc() {
  if (!documents.value.length) addDoc();
}
function removeDoc(i: number) {
  if (documents.value.length <= 1) return; // 기본 1건은 유지
  documents.value.splice(i, 1);
  if (activeIdx.value >= documents.value.length) activeIdx.value = documents.value.length - 1;
}

watch(() => props.pendingDocType, (v) => {
  const type = String(v || '').split(':')[0].toLowerCase();
  if (type === 'edd' || type === 'enhanced') addDoc();
});

function autoTitle(d: EddDocInst): string {
  return d.values.edd_pep_name || EDD_DOC_LABEL;
}
function docTitle(d: EddDocInst): string {
  return (d.title || '').trim() || autoTitle(d);
}
function progressOf(d: EddDocInst): string {
  const filled = EDD_KEYS.filter((k) => d.values[k]).length;
  return `${filled}/${EDD_KEYS.length}`;
}
const overallProgress = computed(() => {
  let filled = 0;
  let total = 0;
  for (const d of documents.value) {
    total += EDD_KEYS.length;
    filled += EDD_KEYS.filter((k) => d.values[k]).length;
  }
  return total === 0 ? 0 : Math.round((filled / total) * 100);
});
watch(overallProgress, (v) => emit('progress-change', v), { immediate: true });

const ZOOM_KEY = 'edd.docZoom';
const ZOOM_MIN = 100;
const ZOOM_MAX = 175;
const ZOOM_STEP = 25;
function clampZoom(n: number) {
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(n / ZOOM_STEP) * ZOOM_STEP));
}
function readStoredZoom(): number {
  try {
    const n = Number(localStorage.getItem(ZOOM_KEY));
    if (Number.isFinite(n)) return clampZoom(n);
  } catch { /* ignore */ }
  return 100;
}
const docZoom = ref(readStoredZoom());
const canZoomIn = computed(() => docZoom.value < ZOOM_MAX);
const canZoomOut = computed(() => docZoom.value > ZOOM_MIN);
function setDocZoom(n: number) {
  docZoom.value = clampZoom(n);
  try { localStorage.setItem(ZOOM_KEY, String(docZoom.value)); } catch { /* ignore */ }
}
function zoomIn() { setDocZoom(docZoom.value + ZOOM_STEP); }
function zoomOut() { setDocZoom(docZoom.value - ZOOM_STEP); }
function zoomReset() { setDocZoom(100); }

const activeKey = ref('');
const activeCheckbox = ref('');
watch(activeIdx, () => { activeCheckbox.value = activeDoc.value?.checkbox || ''; });

const inputKey = ref('');
const inputText = ref('');
const dateMenu = ref(false);

function activate(key: string) {
  activeKey.value = key;
  const ft = typeOf(key);
  if (ft === 'person') {
    emit('request-person-lookup', { fieldGroup: 'edd', key, docIndex: activeIdx.value, defaultRole: null });
    inputKey.value = '';
  } else {
    inputKey.value = key;
    inputText.value = activeDoc.value?.values[key] || '';
    if (ft === 'date') dateMenu.value = true;
  }
}
function onPickDate(d: any) {
  const date = Array.isArray(d) ? d[0] : d;
  if (!date) return;
  const dt = new Date(date);
  inputText.value = Number.isNaN(dt.getTime())
    ? String(date)
    : `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
  dateMenu.value = false;
  commitInput();
}
function commitInput() {
  if (activeDoc.value && inputKey.value) activeDoc.value.values[inputKey.value] = inputText.value.trim();
  inputKey.value = '';
  activeKey.value = '';
}
function skipInput() { inputKey.value = ''; activeKey.value = ''; }
function setCheckbox(v: string) {
  if (!activeDoc.value) return;
  const nv = activeDoc.value.checkbox === v ? '' : v;
  activeDoc.value.checkbox = nv;
  activeCheckbox.value = nv;
}
function setFieldValue(key: string, val: string) {
  if (activeDoc.value) activeDoc.value.values[key] = val;
}

const pendingPickValue = ref('');
watch(() => props.pendingPick, (p) => { pendingPickValue.value = p?.value || ''; });
function placePick(key: string): boolean {
  if (!pendingPickValue.value || !activeDoc.value) return false;
  activeDoc.value.values[key] = pendingPickValue.value;
  pendingPickValue.value = '';
  return true;
}

function openAddress(key: string) {
  if (props.studio) emit('request-address-input', key);
}

const stubRowCount = ref(0);
provide(CDD_DOC_CTX, {
  valueOf: (k) => activeDoc.value?.values[k] || '',
  labelOf: labelize,
  typeOf: (k) => typeOf(k) as any,
  activeKey,
  activate,
  setFieldValue,
  openAddress,
  checkboxValue: activeCheckbox,
  setCheckbox,
  comboOptionsOf: () => [],
  shareholderRowCount: stubRowCount,
  boRowCount: stubRowCount,
  addShareholderRow: () => {},
  removeShareholderRow: () => {},
  addBoRow: () => {},
  removeBoRow: () => {},
  pendingPickValue,
  placePick,
});

async function applyPersonToDoc(doc: EddDocInst, key: string, a: any, ctx?: Record<string, any> | null) {
  const name =
    a.name ||
    a.contactName ||
    [a.firstName, a.middleName, a.surname].filter(Boolean).join(' ') ||
    '';
  doc.values[key] = name;

  const pickedRole = String(
    a.positionName ||
    a.POSITION_NAME ||
    a.role ||
    ctx?.selectedRole ||
    ctx?.defaultRole ||
    '',
  ).trim();
  const skipPicked = !pickedRole || /^contact$/i.test(pickedRole);

  if (key === 'edd_senior_mgmt_name') {
    if (!skipPicked) doc.values.edd_senior_mgmt_designation = pickedRole;
    return;
  }

  if (key !== 'edd_pep_name') return;

  // 단일 선택 직책 먼저 표시 → entity 전체 직책/BO 조회 후 덮어씀
  if (!skipPicked) doc.values.edd_pep_role = pickedRole;

  const ecd = entityCd.value;
  if (!ecd) return;

  try {
    const info = await resolveEddPersonEntityInfo(ecd, {
      contactCd: a.contactCd || a.CONTACT_CD || '',
      name,
      positionName: pickedRole,
    });
    if (info.rolesText) doc.values.edd_pep_role = info.rolesText;
    // 사람 변경 시 Additional Information 도 항상 갱신 (이전 사람 BO 정보 잔존 방지)
    doc.values.edd_additional_info = info.additionalInfo || '';
  } catch (e) {
    console.warn('[EDD] multi-role / BO enrich failed', e);
  }
}

watch(() => props.selectedPerson, (p) => {
  const ctx = props.activePersonContext;
  if (!p || !ctx || ctx.fieldGroup !== 'edd') return;
  const doc = documents.value[ctx.docIndex];
  if (!doc) return;
  void applyPersonToDoc(doc, ctx.key, p as any, ctx);
});

const editMode = computed(() => !!props.editTaskId);
const taskConfigSteps = ref<string[]>([]);
const creating = ref(false);
const progress = ref('');
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; });
watch(progress, (v) => { presetProgress.message = v; });

onMounted(async () => {
  if (props.editTaskId) {
    setupDone.value = true;
  } else {
    ensureDefaultDoc();
  }
  const code = props.selection?.task?.TASK_CODE;
  if (code) {
    try {
      const cfg = await getTaskConfigByCode(code);
      taskConfigSteps.value = (cfg as any)?.processSteps || [];
    } catch { /* noop */ }
  }
  if (!documents.value.length) ensureDefaultDoc();
});

const availableTemplates = computed(() => (props.selection?.documents || []).filter((d) => d.tmpId));
function resolveTmpId(): number | null {
  const docs = availableTemplates.value;
  const hit = docs.find((d) => /edd|enhanced/i.test(d.templateName || ''));
  if (hit?.tmpId) return hit.tmpId;
  if (docs.length === 1) return docs[0].tmpId;
  return null;
}

async function handleCreateTask() {
  if (creating.value) return;
  if (!documents.value.length) { progress.value = t('edd.errNoDoc'); return; }
  if (!effectiveTitle.value) { progress.value = t('edd.errNoTitle'); return; }
  if (!entityCd.value) { progress.value = t('edd.errNoEntity'); return; }

  const tmpId = resolveTmpId();
  if (!tmpId) {
    const avail = availableTemplates.value.map((x) => x.templateName).join(', ') || '(none)';
    progress.value = `${t('edd.errNoTemplate')} — [${avail}]`;
    return;
  }

  creating.value = true;
  progress.value = editMode.value ? t('edd.updating') : t('edd.creating');
  try {
    const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
    const taskFlow = {
      createDocument: steps.length === 0 ? true : steps.includes('doc'),
      requestTSign: steps.includes('t-sign'),
      proceedWithEFiling: steps.includes('e-filing'),
    };
    const officerData = { entityCd: entityCd.value, presetForm: gatherState() };

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = `${t('edd.errCreate')}: invalid edit task id`; creating.value = false; return; }
      try {
        await fetch(`/api/startTask/tasks/${taskId}/data`, {
          method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ taskData: { formData: officerData } }),
        });
        await fetch(`/api/startTask/tasks/${taskId}/title`, {
          method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ taskTitle: effectiveTitle.value }),
        });
      } catch (err: any) {
        progress.value = `${t('edd.errCreate')}: ${err?.message || 'update failed'}`;
        creating.value = false;
        return;
      }
    } else {
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityCd: entityCd.value,
          taskTypeCd: props.selection.task.TASK_CODE,
          taskTypeName: props.selection.task.TASK_NAME,
          taskTitle: effectiveTitle.value,
          taskFlow,
          officerData,
        }),
      }).then((r) => r.json());
      taskId = taskRes?.data?.taskId || taskRes?.taskId;
      if (!taskId) {
        const be = taskRes?.error?.message || taskRes?.message || 'no taskId';
        progress.value = `${t('edd.errCreate')}: ${be}`;
        creating.value = false;
        return;
      }
    }
    progress.value = t('edd.savingDocs');

    const docsToSave = documents.value.map((d) => {
      const signers = Array.isArray(d.signers) ? d.signers : [];
      return {
        templateId: tmpId,
        label: `${EDD_DOC_LABEL} - ${docTitle(d)}`,
        personName: docTitle(d),
        entityCd: entityCd.value,
        formData: buildEddVariables(d),
        signers: signers.length ? signers : undefined,
      };
    });
    try {
      const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }),
      }).then((r) => r.json());
      if (!saveRes?.success) progress.value = `${t('edd.warnDocs')}: ${saveRes?.message || 'unknown'}`;
    } catch (err: any) {
      console.warn('[EDD] document save failed:', err);
    }

    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      });
    } catch { /* ignore */ }
    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }),
      });
    } catch { /* ignore */ }
    try {
      progress.value = t('edd.nextScreen');
      const [taskDetailRes, docListRes] = await Promise.all([
        fetch(`/api/startTask/tasks/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
        fetch(`/api/startTask/doc-cust-save/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
      ]);
      sessionStorage.setItem(`presetTaskDetailPrefetch:${taskId}`, JSON.stringify({ taskDetail: taskDetailRes, docList: docListRes }));
    } catch { /* ignore */ }

    emit('task-created', { taskId, taskTitle: effectiveTitle.value });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (e: any) {
    console.error('[EDD] create task 실패', e);
    progress.value = `${t('edd.errCreate')}: ${e?.message || e}`;
  } finally {
    creating.value = false;
  }
}

function gatherState() { return { documents: documents.value }; }
function restoreState(s: any) {
  if (s?.documents?.length) {
    documents.value = s.documents.map((d: any) => ({
      ...d,
      signers: Array.isArray(d?.signers) ? d.signers : [],
    }));
    activeIdx.value = 0;
  } else {
    ensureDefaultDoc();
  }
  setupDone.value = true;
}
defineExpose({ gatherState, restoreState, addDoc, createTask: handleCreateTask });
</script>

<template>
  <div class="cdd-panel" :class="{ 'cdd-studio': studio }">
    <div v-if="!studio" class="co-header">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME }}</div>
        <div class="co-subtitle">{{ t('edd.subtitle') }}</div>
      </div>
      <v-btn variant="text" size="small" class="text-none" @click="emit('back')">
        <v-icon start size="16">mdi-arrow-left</v-icon>{{ t('edd.back') }}
      </v-btn>
    </div>

    <!-- Wizard: Title → Entity (required) → editor -->
    <div v-if="showWizard" class="cdd-wizard">
      <div class="wz-steps">
        <div class="wz-step" :class="{ active: wizardStep === 1, done: wizardStep > 1 }"><span class="wz-num">1</span>{{ t('edd.wzStepTitle') }}</div>
        <div class="wz-bar" :class="{ done: wizardStep > 1 }" />
        <div class="wz-step" :class="{ active: wizardStep === 2 }"><span class="wz-num">2</span>{{ t('edd.wzStepEntity') }}</div>
      </div>

      <div v-if="wizardStep === 1" class="wz-card">
        <div class="wz-h">{{ t('edd.wzTitleH') }}</div>
        <div class="wz-sub">{{ t('edd.wzTitleSub') }}</div>
        <v-text-field
          v-model="titleModel" :placeholder="t('edd.taskTitlePh')" variant="outlined" density="comfortable"
          hide-details autofocus class="wz-input" @keydown.enter="effectiveTitle && wizardNext()"
        />
        <div class="wz-actions">
          <v-spacer />
          <v-btn color="primary" class="text-none" :disabled="!effectiveTitle" append-icon="mdi-arrow-right" @click="wizardNext">{{ t('edd.next') }}</v-btn>
        </div>
      </div>

      <div v-else class="wz-card">
        <div class="wz-h">{{ t('edd.wzEntityH') }}</div>
        <div class="wz-sub">{{ t('edd.wzEntitySub') }}</div>
        <div v-if="entityData?.companyName || entityData?.entityName" class="wz-entity">
          <v-icon color="primary">mdi-domain</v-icon>
          <span>{{ entityData?.companyName || entityData?.entityName }}</span>
          <v-btn variant="tonal" size="small" class="text-none ml-auto" @click="emit('change-entity')">{{ t('edd.change') }}</v-btn>
        </div>
        <v-btn v-else variant="tonal" color="primary" class="text-none wz-selectbtn" prepend-icon="mdi-domain" @click="emit('change-entity')">{{ t('edd.wzSelectEntity') }}</v-btn>
        <div v-if="progress" class="wz-err">{{ progress }}</div>
        <div class="wz-actions">
          <v-btn variant="text" class="text-none" prepend-icon="mdi-arrow-left" @click="wizardBack">{{ t('edd.back') }}</v-btn>
          <v-spacer />
          <v-btn color="primary" class="text-none" :disabled="!entityCd" append-icon="mdi-arrow-right" @click="wizardNext">{{ t('edd.next') }}</v-btn>
        </div>
      </div>
    </div>

    <template v-else>
      <div v-if="!studio" class="co-title-row">
        <span class="ct-label">{{ t('edd.taskTitle') }}</span>
        <v-text-field
          v-model="titleModel" :placeholder="t('edd.taskTitlePh')" variant="outlined" density="compact"
          hide-details class="ct-input"
        />
      </div>

      <div v-if="!studio" class="co-entity">
        <v-icon size="18" color="primary">mdi-domain</v-icon>
        <span>{{ entityData?.companyName || entityData?.entityName || t('edd.entity') }}</span>
        <v-btn variant="tonal" size="x-small" class="text-none ml-auto" @click="emit('change-entity')">{{ t('edd.change') }}</v-btn>
      </div>

      <div v-if="inputKey" class="co-inputbar">
        <div class="lbl">{{ labelize(inputKey) }} <span v-if="isOptional(inputKey)" class="opt">({{ t('edd.optional') }})</span></div>
        <div class="row">
          <v-menu v-if="typeOf(inputKey) === 'date'" v-model="dateMenu" :close-on-content-click="false">
            <template #activator="{ props: mp }">
              <v-text-field v-bind="mp" v-model="inputText" placeholder="DD/MM/YYYY" variant="outlined" density="compact" hide-details append-inner-icon="mdi-calendar" />
            </template>
            <v-date-picker @update:model-value="onPickDate" />
          </v-menu>
          <v-text-field v-else v-model="inputText" :placeholder="labelize(inputKey)" variant="outlined" density="compact" hide-details autofocus @keydown.enter="commitInput" />
          <v-btn color="primary" size="small" variant="flat" @click="commitInput">{{ t('edd.set') }}</v-btn>
          <v-btn v-if="typeOf(inputKey) === 'date'" variant="text" size="small" @click="skipInput">{{ t('edd.skip') }}</v-btn>
        </div>
      </div>

      <div class="co-docs">
        <div
          v-for="(d, i) in documents" :key="i"
          class="doc-tab" :class="{ active: i === activeIdx }"
          @click="activeIdx = i"
        >
          <span class="dt-type">{{ t('edd.docType') }}</span>
          <input
            v-if="renamingIdx === i" v-model="d.title" class="dt-rename" :placeholder="autoTitle(d)" autofocus
            @click.stop @keydown.enter="renamingIdx = -1" @keydown.esc="renamingIdx = -1" @blur="renamingIdx = -1"
          />
          <span v-else class="dt-name" :title="t('edd.renameHint')" @dblclick.stop="renamingIdx = i">{{ docTitle(d) }}</span>
          <span class="dt-prog">{{ progressOf(d) }}</span>
          <v-icon size="13" class="dt-edit" :title="t('edd.rename')" @click.stop="renamingIdx = i">mdi-pencil-outline</v-icon>
          <v-icon size="13" class="dt-del" @click.stop="removeDoc(i)">mdi-close</v-icon>
        </div>
        <v-btn size="small" variant="tonal" color="primary" class="text-none" prepend-icon="mdi-plus" @click="addDoc">{{ t('edd.addEdd') }}</v-btn>
        <div v-if="activeDoc" class="cdd-zoom" role="group" :aria-label="t('edd.zoomIn')">
          <button type="button" class="cdd-zoom-btn" :disabled="!canZoomOut" :title="t('edd.zoomOut')" @click="zoomOut">A−</button>
          <button type="button" class="cdd-zoom-pct" :title="t('edd.zoomReset')" @click="zoomReset">{{ docZoom }}%</button>
          <button type="button" class="cdd-zoom-btn" :disabled="!canZoomIn" :title="t('edd.zoomIn')" @click="zoomIn">A+</button>
        </div>
      </div>

      <div class="co-doc-body">
        <div v-if="!activeDoc" class="cc-studio-empty">
          <v-icon size="34" color="#c4ccd6">mdi-file-document-plus-outline</v-icon>
          <div class="ccs-title">{{ t('edd.emptyTitle') }}</div>
          <div class="ccs-sub">{{ t('edd.emptySub') }}</div>
          <v-btn color="primary" class="text-none mt-3" prepend-icon="mdi-plus" @click="addDoc">{{ t('edd.addEdd') }}</v-btn>
        </div>
        <div v-else>
          <div class="co-doctitle">
            <span class="dt-label">{{ t('edd.documentTitle') }}</span>
            <v-text-field v-model="activeDoc.title" :placeholder="autoTitle(activeDoc)" variant="outlined" density="compact" hide-details class="dt-input" />
          </div>
          <div class="paper-zoom" :style="{ zoom: docZoom / 100 }">
            <div class="paper">
              <EddDoc
                :key="activeIdx"
                :entity-cd="entityCd"
                :signers="activeDoc.signers || []"
                @update:signers="setActiveSigners"
              />
            </div>
          </div>
        </div>
      </div>

      <div v-if="!studio || progress" class="co-footer">
        <v-btn v-if="!studio" variant="outlined" class="text-none" @click="emit('back')">{{ t('edd.back') }}</v-btn>
        <div class="co-foot-right">
          <span v-if="progress" class="co-progress" :class="{ err: progress.includes('⚠') }">{{ progress }}</span>
          <v-btn
            v-if="!studio"
            color="primary" class="text-none"
            :loading="creating"
            :disabled="creating || !documents.length || !entityCd"
            :prepend-icon="editMode ? 'mdi-content-save' : 'mdi-check'"
            @click="handleCreateTask"
          >{{ editMode ? t('edd.updateTask') : t('edd.createTask') }}</v-btn>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.cdd-panel { display:flex; flex-direction:column; }
.cdd-studio { flex:0 0 auto; width:min(1000px, 100%); background:transparent; }
.cdd-studio .co-docs { background:transparent; border-bottom:none; padding-left:0; padding-right:0; }
.cdd-studio .co-doctitle { max-width:none; }
.cdd-studio .co-doc-body { background:transparent; padding:0; }
.co-header { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; border-bottom:1px solid #eef0f6; }
.co-title { font-weight:700; font-size:15px; } .co-subtitle { font-size:12px; color:#8a94a6; }
.co-title-row { display:flex; align-items:center; gap:10px; padding:10px 14px; border-bottom:1px solid #eef0f6; }
.co-title-row .ct-label { font-size:12px; font-weight:700; color:#5b6472; white-space:nowrap; }
.co-title-row .ct-input { max-width:520px; }
.co-entity { display:flex; align-items:center; gap:8px; background:#f0f5ff; padding:8px 14px; font-size:13px; }
.co-inputbar { padding:10px 14px; background:#fafbfe; border-bottom:1px solid #eef0f6; }
.co-inputbar .lbl { font-size:12px; font-weight:700; color:#1B84FF; margin-bottom:6px; } .co-inputbar .opt { color:#8a94a6; font-weight:400; }
.co-inputbar .row { display:flex; align-items:center; gap:8px; }
.co-docs { display:flex; align-items:center; gap:8px; padding:10px 14px; border-bottom:1px solid #eef0f6; overflow-x:auto; }
.cdd-zoom { margin-left:auto; flex:0 0 auto; display:inline-flex; align-items:center; gap:2px; background:#f2f4f6; border-radius:10px; padding:2px; }
.cdd-zoom-btn, .cdd-zoom-pct { border:none; background:transparent; cursor:pointer; font-weight:800; color:#4b5563; border-radius:8px; line-height:1; }
.cdd-zoom-btn { min-width:36px; height:32px; font-size:15px; }
.cdd-zoom-btn:hover:not(:disabled) { background:#fff; color:#191f28; }
.cdd-zoom-btn:disabled { opacity:0.35; cursor:default; }
.cdd-zoom-pct { min-width:52px; height:32px; font-size:12px; color:#3182f6; }
.cdd-zoom-pct:hover { background:#fff; }
.paper-zoom { max-width:820px; margin:0 auto; }
.paper-zoom .paper { max-width:none; margin:0; }
.doc-tab { display:flex; align-items:center; gap:6px; border:1px solid #e2e6ef; border-radius:8px; padding:5px 9px; cursor:pointer; white-space:nowrap; }
.doc-tab.active { border-color:#1B84FF; background:#eaf3ff; }
.dt-type { font-size:10px; font-weight:700; color:#1B84FF; background:#eaf3ff; border-radius:5px; padding:1px 5px; }
.dt-name { font-size:12px; font-weight:600; max-width:160px; overflow:hidden; text-overflow:ellipsis; cursor:text; }
.dt-rename { font-size:12px; font-weight:600; border:1px solid #1B84FF; border-radius:5px; padding:1px 5px; width:150px; outline:none; }
.dt-prog { font-size:11px; color:#8a94a6; } .dt-del { color:#b3bccd; } .dt-edit { color:#b3bccd; } .dt-edit:hover { color:#1B84FF; }
.co-doc-body { padding:20px; background:#eceff5; }
.cc-studio-empty { padding:56px 20px; text-align:center; display:flex; flex-direction:column; align-items:center; gap:8px; }
.cc-studio-empty .ccs-title { font-size:16px; font-weight:800; color:#191f28; margin-top:4px; }
.cc-studio-empty .ccs-sub { font-size:13px; color:#8b95a1; }
.cdd-wizard { padding:28px 20px 40px; }
.wz-steps { display:flex; align-items:center; justify-content:center; gap:10px; max-width:560px; margin:0 auto 28px; }
.wz-step { display:flex; align-items:center; gap:7px; font-size:13px; font-weight:600; color:#9aa3b2; white-space:nowrap; }
.wz-step .wz-num { width:22px; height:22px; border-radius:50%; background:#e2e6ef; color:#8a94a6; display:inline-flex; align-items:center; justify-content:center; font-size:12px; }
.wz-step.active { color:#1B84FF; } .wz-step.active .wz-num { background:#1B84FF; color:#fff; }
.wz-step.done { color:#22a06b; } .wz-step.done .wz-num { background:#22a06b; color:#fff; }
.wz-bar { flex:1; height:2px; background:#e2e6ef; max-width:60px; } .wz-bar.done { background:#22a06b; }
.wz-card { max-width:460px; margin:0 auto; background:#fff; border:1px solid #eef0f6; border-radius:14px; padding:28px 26px; box-shadow:0 4px 20px rgba(20,30,60,.06); text-align:center; }
.wz-h { font-size:17px; font-weight:700; color:#1f2937; }
.wz-sub { font-size:13px; color:#8a94a6; margin-top:6px; margin-bottom:20px; }
.wz-input { text-align:left; }
.wz-entity { display:flex; align-items:center; gap:10px; background:#f0f5ff; border-radius:10px; padding:12px 14px; font-size:14px; font-weight:600; color:#10305c; }
.wz-selectbtn { width:100%; height:48px; }
.wz-actions { display:flex; align-items:center; gap:8px; margin-top:22px; }
.wz-err { margin-top:12px; font-size:12.5px; color:#d64545; font-weight:600; }
.co-doctitle { max-width:820px; margin:0 auto 12px; display:flex; align-items:center; gap:10px; }
.co-doctitle .dt-label { font-size:12px; font-weight:700; color:#5b6472; white-space:nowrap; }
.co-doctitle .dt-input { background:#fff; border-radius:8px; }
.paper { background:#fff; max-width:820px; margin:0 auto; padding:34px 38px; box-shadow:0 4px 24px rgba(20,30,60,.12); font-family:Arial,'Malgun Gothic',sans-serif; color:#111; font-size:12px; line-height:1.4; }
.co-footer { display:flex; justify-content:space-between; align-items:center; padding:12px 16px; border-top:1px solid #eef0f6; }
.co-foot-right { display:flex; align-items:center; gap:12px; }
.co-progress { font-size:12px; color:#5b6472; max-width:420px; }
.co-progress.err { color:#d64545; font-weight:600; }
</style>

<style>
.cdd-title { text-align:center; font-weight:700; font-size:15px; padding:8px; border:1px solid #000; }
.cdd-tbl { width:100%; border-collapse:collapse; margin-top:-1px; }
.cdd-tbl td, .cdd-tbl th { border:1px solid #000; padding:5px 8px; vertical-align:top; text-align:left; font-weight:400; }
.cdd-sec { font-weight:700; background:#f2f2f2; }
.cdd-lcell { width:42%; }
.cdd-cn { color:#333; }
.cdd-intro { font-size:11px; line-height:1.45; }
.cdd-cbx { display:inline-flex; align-items:center; margin-right:14px; white-space:nowrap; cursor:pointer; }
.cdd-cbx .box { display:inline-flex; margin-right:4px; vertical-align:-3px; }
</style>
